import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { useEffect, useState } from "react";
import { initReactI18next, useTranslation as useTranslationOrg } from "react-i18next";
import { getOptions, languages } from "./settings";

const runsOnServerSide = typeof window === "undefined";

const customResourceLoader = async (language, namespace) => {
  const isDev = import.meta.env.DEV;
  const url = isDev ? `${window.location.origin}/membersapp` : window.location.origin;
  const filePath = `${url}/ee-static/membersapp/languages/${language}/${namespace}.json`;

  try {
    const originalModule = await import(`./locales/${language}/${namespace}.json`);
    const originalData = originalModule.default;

    if (runsOnServerSide) {
      return { [language]: { [namespace]: originalData } };
    }

    const customerResponse = await fetch(filePath);
    const customerData = await customerResponse.json();

    const mergedData = { ...originalData, ...customerData };
    return { [language]: { [namespace]: mergedData } };
  } catch (error) {
    console.error("Error loading language resource:", error);
    const originalModule = await import(`./locales/${language}/${namespace}.json`);
    return { [language]: { [namespace]: originalModule.default } };
  }
};

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(async (language, namespace) => {
      const custom = await customResourceLoader(language, namespace);
      return custom[language][namespace];
    })
  )
  .init({
    ...getOptions(),
    lng: undefined,
    detection: {
      order: ["cookie", "navigator"],
      lookupCookie: "i18next",
      convertDetectedLanguage: (lng) => String(lng).split("-")[0],
      caches: []
    },
    preload: runsOnServerSide ? languages : []
  });

/**
 * @param {string} [lng] - Optional short language override. Prefer omitting so
 *   i18next uses the cookie/detector language.
 * @param {string|string[]} [ns]
 * @param {object} [options]
 */
export function useTranslation(lng, ns, options) {
  const ret = useTranslationOrg(ns, options);
  const { i18n } = ret;
  const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);

  useEffect(() => {
    if (activeLng === i18n.resolvedLanguage) return;
    setActiveLng(i18n.resolvedLanguage);
  }, [activeLng, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!lng || i18n.resolvedLanguage === lng) return;
    i18n.changeLanguage(lng);
  }, [lng, i18n]);

  if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  }

  return ret;
}
